import React, { useEffect, useMemo, useState } from "react";
import { Badge, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import Constants from "../../constants";
import HttpManager from "../../services/HttpManager";
import EnrollmentsService from "../../services/campus/enrollments.service";
import { getErrorMessage } from "../../common";
import { AdvisorSelection, AvailableScheduleDTO } from "../../models/EnrollmentHistoryDTO";
import AdvisorsPicker from "../../components/enrollment/advisorsPicker";
import SchedulePicker from "../../components/enrollment/schedulePicker";
import { formatLocalDateTime } from "../../components/enrollment/format";
import { sinAcentos, cursoCorto } from "../../utils/cursoCorto";
import { horarioBonito } from "../../utils/horarioBonito";
import { ahoraCdmx } from "../../utils/horaMexico";
import PromocionDirecta, { faltaPromo, ordenPorAncla, promoParaEnviar, promoVacia } from "../../components/acuerdos/promocionDirecta";
import { RequestPromotionDTO } from "../../models/EnrollmentHistoryDTO";
import rotationService from "../../services/v2/rotation.service";
import RotationPicker from "../../components/enrollment/rotationPicker";
import { SiblingSchedulesDTO } from "../../models/RotationDTO";
import courseBlocksService from "../../services/v2/courseBlocks.service";
import { sinTalleresNiEventos } from "../../components/enrollment/cursosNoMatriculables";
import CompositeCoursePicker, { CompositeSelection } from "../../components/enrollment/compositeCoursePicker";

/** Two ways in: schedules of normal courses, or a composite course (Hotelería) through its starting block. */
type EnrollMode = "schedules" | "composite";

/** One picked schedule with its own sign-up note and, if the student will rotate, the sibling schedules allowed. */
interface SchedulePick {
	schedule: AvailableScheduleDTO;
	notes: string;
	//Alumno rotativo: hermanos del horario elegido (mismo módulo y periodo) y los autorizados.
	siblings: SiblingSchedulesDTO | null;
	rotating: boolean;
	authorizedScheduleIds: number[];
}

export interface EnrollStudentModalProps {
	studentId: number;
	studentName: string;
	campusId: number;
	onClose: () => void;
	onEnrolled: () => void;
}

/**
 * Enroll a student from their profile: pick one of the schedules WAPP allows for them (sign-up rules) — or, for a
 * composite course such as Hotelería, the course, the starting block and a real schedule of that block — plus the
 * advisors and a sign-up note (kits are never touched here). Both ways use the same server endpoint as the Horarios
 * screen, so every validation (overdue balance, duplicate module, hour clash) still applies; the composite way adds
 * `enrollCourseId` + `blockId` so the server bills from the composite course.
 */
// Día de la semana que aparece en el nombre o las sesiones del horario, para
// que dentro de una misma fecha los horarios salgan de lunes a domingo.
const DIAS = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];
const ABREVIATURAS = ["lu", "ma", "mi", "ju", "vi", "sa", "do"];

const rangoDia = (texto: string): number => {
	const t = sinAcentos(texto);
	for (let i = 0; i < DIAS.length; i++) {
		if (t.includes(DIAS[i])) {
			return i;
		}
	}
	const abreviatura = t.match(/\b(lu|ma|mi|ju|vi|sa|do)\b/);
	return abreviatura ? ABREVIATURAS.indexOf(abreviatura[1]) : 99;
};

const soloFecha = (valor: string | null | undefined): string => String(valor || "").slice(0, 10);

const comoTexto = (fecha: Date): string => [
	fecha.getFullYear(),
	String(fecha.getMonth() + 1).padStart(2, "0"),
	String(fecha.getDate()).padStart(2, "0")
].join("-");

/** Se matricula gente hasta antes de la segunda clase, así que lo que arrancó esta última semana
 *  sigue contando como "por comenzar" (Esteban, 2026-09-11). */
const DIAS_DE_GRACIA = 7;

/**
 * Orden de los horarios, en tres grupos (Esteban, 2026-09-24):
 *   0. Los que arrancaron en los últimos 7 días: se matricula hasta antes de la segunda clase.
 *   1. Los que están por comenzar, del más próximo al más lejano.
 *   2. Los que ya llevan tiempo corriendo (reingresos a grupos de periodos anteriores), del más
 *      reciente hacia atrás.
 * La tabla pone un título a cada grupo: sin él, "Ene 2027" (último por comenzar) quedaba pegado a
 * "Ago 2026" (primero de los ya comenzados) y parecía fuera de orden.
 * Dentro de una misma fecha se agrupa por curso, módulo y día de la semana.
 */
export const GRUPOS_HORARIO = ["Comenzaron esta semana", "Por comenzar", "Ya comenzaron"];

export const grupoHorario = (schedule: AvailableScheduleDTO): number => {
	const hoy = comoTexto(ahoraCdmx());
	const desde = ahoraCdmx();
	desde.setDate(desde.getDate() - DIAS_DE_GRACIA);
	const fecha = soloFecha(schedule.startDate);
	if (fecha > hoy) {
		return 1;
	}
	return fecha >= comoTexto(desde) ? 0 : 2;
};

export const ordenarHorarios = (horarios: AvailableScheduleDTO[]): AvailableScheduleDTO[] => {
	return [...horarios].sort((a, b) => {
		const grupoA = grupoHorario(a);
		const grupoB = grupoHorario(b);
		if (grupoA !== grupoB) {
			return grupoA - grupoB;
		}
		const fechaA = soloFecha(a.startDate);
		const fechaB = soloFecha(b.startDate);
		if (fechaA !== fechaB) {
			// Esta semana y por comenzar: la fecha más temprana primero. Ya comenzados: el más reciente primero.
			return grupoA === 2 ? (fechaA > fechaB ? -1 : 1) : (fechaA < fechaB ? -1 : 1);
		}
		return (a.courseName || "").localeCompare(b.courseName || "", "es")
			|| (a.levelName || "").localeCompare(b.levelName || "", "es")
			|| (rangoDia(`${a.name} ${a.sessions}`) - rangoDia(`${b.name} ${b.sessions}`))
			|| (a.name || "").localeCompare(b.name || "", "es");
	});
};

const MESES_CORTOS: {[largo: string]: string} = {
	enero: "Ene", febrero: "Feb", marzo: "Mar", abril: "Abr",
	mayo: "May", junio: "Jun", julio: "Jul", agosto: "Ago",
	septiembre: "Sep", octubre: "Oct", noviembre: "Nov", diciembre: "Dic"
};

/** "Septiembre 2026" -> "Sep 2026" */
export const periodoCorto = (nombre: string): string =>
	String(nombre || "").replace(/[A-Za-zÁÉÍÓÚÑáéíóúñ]+/g, (palabra) =>
		MESES_CORTOS[sinAcentos(palabra)] ?? palabra);

/** "Nelson Salvador" + "Castro Rodríguez" -> "Nelson Castro" */
export const maestroCorto = (schedule: AvailableScheduleDTO): string =>
	[schedule.mentorFirstName, schedule.mentorLastName]
		.map((parte) => String(parte || "").trim().split(/\s+/)[0] || "")
		.filter(Boolean)
		.join(" ") || String(schedule.mentorName || "").trim();

/*
	Solo se ofrecen los horarios donde de verdad se puede matricular a alguien:
	activos y que no hayan terminado. Los que ya acabaron seguian saliendo en la
	lista y no sirven para nada aqui. Uno por comenzar con cero alumnos si sale:
	para eso esta la matricula. (Esteban, 2026-09-16)
*/
export const seLePuedeMatricular = (schedule: AvailableScheduleDTO): boolean => {
	if (String(schedule.status || "").toLowerCase() !== "activo") {
		return false;
	}

	const fin = String(schedule.endDate || "").slice(0, 10);

	if (!fin) {
		return true;
	}

	const hoy = ahoraCdmx();
	const hoyTexto = hoy.getFullYear() + "-"
		+ String(hoy.getMonth() + 1).padStart(2, "0") + "-"
		+ String(hoy.getDate()).padStart(2, "0");

	return fin >= hoyTexto;
};

/** Segmented toggle: the active mode is filled orange, the other one outlined. */
const modoEstilo = (activo: boolean): React.CSSProperties => activo
	? { background: "var(--wa-acento)", borderColor: "var(--wa-acento)", color: "#FFFFFF" }
	: { background: "var(--wa-superficie)", borderColor: "var(--wa-filo)", color: "var(--wa-tinta)" };

const EnrollStudentModal: React.FC<EnrollStudentModalProps> = ({ studentId, studentName, campusId, onClose, onEnrolled }) => {
	const http = HttpManager.getInstance();
	const [schedules, setSchedules] = useState<AvailableScheduleDTO[] | null>(null);
	const [filter, setFilter] = useState<string>("");
	//Se puede matricular en varios horarios de una vez, cada uno con su propia nota (Esteban, 2026-09-22).
	const [picks, setPicks] = useState<SchedulePick[]>([]);
	//null = todavía no eligen asesoras; [] = eligieron "Ninguno" (Esteban, 2026-09-11).
	const [advisors, setAdvisors] = useState<AdvisorSelection[] | null>(null);
	const [intentoGuardar, setIntentoGuardar] = useState<boolean>(false);
	//A1 (cobros, 2026-10-08): el paso Promoción también al matricular desde el perfil («Ninguna» vale).
	const [promo, setPromo] = useState<RequestPromotionDTO>(promoVacia());
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<boolean>(false);
	//Curso compuesto: la matrícula es del curso (lo que cobra) y queda en el horario del bloque donde empieza.
	const [mode, setMode] = useState<EnrollMode>("schedules");
	const [composite, setComposite] = useState<CompositeSelection | null>(null);
	const [compositeNotes, setCompositeNotes] = useState<string>("");

	const switchMode = (next: EnrollMode) => {
		if (busy || next === mode) {
			return;
		}
		setMode(next);
		setPicks([]);
		setComposite(null);
		setCompositeNotes("");
		setError(null);
	};

	useEffect(() => {
		http.scheduleService.getAvailableSchedules(campusId, studentId)
			.then((response) => sinTalleresNiEventos(campusId, response.data || []))
			.then((lista) => setSchedules(lista))
			.catch((e) => { setSchedules([]); setError(getErrorMessage(e)); });
	}, [campusId, studentId]);

	const isPicked = (id: number) => picks.some((pick) => pick.schedule.id === id);

	const updatePick = (id: number, change: Partial<SchedulePick>) =>
		setPicks((list) => list.map((pick) => pick.schedule.id === id ? { ...pick, ...change } : pick));

	const select = (schedule: AvailableScheduleDTO) => {
		setError(null);
		if (isPicked(schedule.id)) {
			setPicks((list) => list.filter((pick) => pick.schedule.id !== schedule.id));
			return;
		}
		setPicks((list) => [...list, { schedule, notes: "", siblings: null, rotating: false, authorizedScheduleIds: [] }]);
		//Hermanos del horario para "Rotativo". Si el horario ya se quitó cuando llega la respuesta, updatePick no encuentra nada y no pasa nada.
		rotationService.getSiblings(campusId, schedule.id)
			.then((response) => updatePick(schedule.id, { siblings: response.data }))
			.catch(() => updatePick(schedule.id, { siblings: { homeSchedule: { id: schedule.id, name: schedule.name, weeklyHours: 0 }, options: [] } }));
	};

	const setPickNotes = (id: number, value: string) => updatePick(id, { notes: value });

	const pickIds = picks.map((pick) => pick.schedule.id);
	//La promoción se elige sobre lo que se va a matricular: los horarios, o el horario de arranque del curso compuesto.
	const idsElegidos: number[] = mode === "composite" ? (composite ? [composite.schedule.id] : []) : pickIds;
	const faltaPromocion: boolean = idsElegidos.length > 0 && faltaPromo(promo, idsElegidos);
	const hayEleccion: boolean = mode === "composite" ? composite !== null : picks.length > 0;
	const faltanObligatorios: boolean = faltaPromocion || advisors === null || (mode === "composite"
		? compositeNotes.trim() === ""
		: picks.some((pick) => pick.notes.trim() === ""));

	/**
	 * The v1 POST URL wants the SCHEDULE's own course/level/id (Gastronomía / Gastronomía 1 / Domingo 11 am):
	 * the block DTO does not always carry the module's course id, so it is resolved through `_blockTargets`.
	 */
	const resolveCompositeTarget = async (selection: CompositeSelection): Promise<{ course_id: number; level_id: number; id: number }> => {
		const levelId = selection.schedule.levelId;
		let courseId: number | null | undefined = selection.block.targetCourseId;
		if (courseId == null) {
			const targets = await courseBlocksService.getTargetsCached(campusId);
			courseId = targets.find((target) => target.levelId === levelId)?.courseId;
		}
		if (courseId == null) {
			throw new Error(Constants.Common.moduleCourseUnknown);
		}
		return { course_id: courseId, level_id: levelId, id: selection.schedule.id };
	};

	const enroll = async () => {
		if (!hayEleccion) {
			setError(Constants.Common.selectScheduleFirst);
			return;
		}
		//Asesoras y nota son obligatorias; "Ninguno" es una respuesta válida (Esteban, 2026-09-11).
		if (faltanObligatorios) {
			setIntentoGuardar(true);
			setError(advisors === null ? Constants.Common.advisorsRequired : faltaPromocion ? Constants.Common.promotionRequired : Constants.Common.signupNoteRequired);
			return;
		}
		//Matricular no se deshace solo: se confirma antes, con los cursos a la vista (Esteban, 2026-09-22).
		const resumen = mode === "composite" && composite
			? "· " + cursoCorto(composite.course.name) + " — " + Constants.Common.blockName + ": " + composite.block.name
				+ " — " + horarioBonito(composite.schedule.name, { plural: true, separador: " " })
			: picks.map((pick) => "· " + cursoCorto(pick.schedule.courseName) + " — " + horarioBonito(pick.schedule.name, { plural: true, separador: " " })).join("\n");
		if (!window.confirm("Se va a matricular a " + studentName + " en:\n\n" + resumen + "\n\n¿Confirmas?")) {
			return;
		}
		setBusy(true);
		setError(null);
		try {
			const advisorsBody = (advisors ?? []).map((advisor) => ({ userId: advisor.userId, sharePercentage: advisor.sharePercentage }));
			if (mode === "composite" && composite) {
				const target = await resolveCompositeTarget(composite);
				const result = await EnrollmentsService.addEnrollments(target, {
					studentWithKitIds: String(studentId), // kits are NOT assigned here (Esteban, 2026-09-05); Horarios keeps its own kit flow
					notes: compositeNotes.trim(),
					advisors: advisorsBody,
					//The server bills from the composite course and records where the student started.
					enrollCourseId: composite.course.id,
					blockId: composite.block.id,
					promotions: { [String(studentId)]: promoParaEnviar(promo, composite.schedule.id, {}, true) },
					origin: "perfil"
				});
				if (result.status !== 200 && result.status !== 204) {
					throw new Error(result.statusText);
				}
			} else {
				//El curso que es «el otro curso» de un 50 % o la base de un 2x1 se matricula primero.
				const creadas: Record<number, number> = {};
				const orden = ordenPorAncla(pickIds, promo);
				for (const [i, sid] of orden.entries()) {
					const pick = picks.find((x) => x.schedule.id === sid)!;
					const result = await EnrollmentsService.addEnrollments(
						{ course_id: pick.schedule.courseId, level_id: pick.schedule.levelId, id: pick.schedule.id },
						{
							studentWithKitIds: String(studentId), // kits are NOT assigned here (Esteban, 2026-09-05); Horarios keeps its own kit flow
							notes: pick.notes.trim(),
							advisors: advisorsBody,
							promotions: { [String(studentId)]: promoParaEnviar(promo, sid, creadas, i === 0) },
							origin: "perfil",
							rotating: pick.rotating && pick.authorizedScheduleIds.length > 0,
							authorizedScheduleIds: pick.rotating ? pick.authorizedScheduleIds : []
						}
					);
					if (result.status !== 200 && result.status !== 204) {
						throw new Error(result.statusText);
					}
					const eid = Number(result?.data?.enrollmentIds?.[String(studentId)] || 0);
					if (eid > 0) creadas[sid] = eid;
				}
			}
			setSuccess(true);
			setTimeout(onEnrolled, 900);
		} catch (e) {
			setError(getErrorMessage(e));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal show onHide={busy ? undefined : onClose} size="xl" centered scrollable>
			<Modal.Header closeButton={!busy}>
				<Modal.Title style={{ fontSize: 18 }}>
					{Constants.Common.enrollStudent}: <b>{studentName}</b>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ textAlign: "left" }}>
				{error ? <div className="alert alert-danger" style={{ fontSize: 13 }}>{error}</div> : ""}
				{success ? <div className="alert alert-success" style={{ fontSize: 13 }}>{Constants.Common.enrollSuccess}</div> : ""}
				{/* Dos caminos: horarios de cursos normales, o un curso compuesto (Hotelería) por el bloque donde empieza. */}
				<div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
					<div className="btn-group btn-group-sm" role="group">
						<button type="button" className="btn rounded-start-pill" style={modoEstilo(mode === "schedules")} disabled={busy} onClick={() => switchMode("schedules")}>
							{Constants.Common.schedulesTab}
						</button>
						<button type="button" className="btn rounded-end-pill" style={modoEstilo(mode === "composite")} disabled={busy} onClick={() => switchMode("composite")}>
							<FontAwesomeIcon icon={solid("layer-group")} />&nbsp;{Constants.Common.compositeCourse}
						</button>
					</div>
				</div>
				{mode === "composite" ? (
					<>
						<h6 style={{ margin: "0 0 8px" }}>{Constants.Common.compositeCourse}</h6>
						<CompositeCoursePicker campusId={campusId} value={composite} onChange={setComposite} disabled={busy} idPrefix="enrollComposite" />
					</>
				) : (
					<SchedulePicker schedules={schedules} picked={picks.map((pick) => pick.schedule.id)} onToggle={select}
						filter={filter} onFilter={setFilter} disabled={busy} />
				)}
				{hayEleccion ? (
					<div className="row" style={{ marginTop: 14, rowGap: 10 }}>
						<div className="col-md-6">
							<AdvisorsPicker campusId={campusId} value={advisors} onChange={setAdvisors} disabled={busy} invalid={intentoGuardar} />
						</div>
						<div className="col-md-12">
							<PromocionDirecta campusId={campusId} studentId={studentId} studentName={studentName} scheduleIds={idsElegidos}
								value={promo} onChange={setPromo} busy={busy} marcar={intentoGuardar} />
						</div>
						{mode === "composite" ? (
							<div className="col-md-6">
								<label htmlFor="enrollNotesComposite" style={{ position: "relative", display: "block" }}>
									{Constants.Common.signupNote} <span style={{ color: "var(--wa-oro)" }}>*</span>
								</label>
								<textarea id="enrollNotesComposite" className="form-control" style={{ minHeight: 110 }} maxLength={2000} disabled={busy}
									placeholder={Constants.Common.signupNotePlaceholder} value={compositeNotes} onChange={(event) => setCompositeNotes(event.target.value)} />
								{intentoGuardar && compositeNotes.trim() === ""
									? <div style={{ fontSize: 13, color: "var(--wa-oro)", marginTop: 4 }}>{Constants.Common.signupNoteRequired}</div>
									: ""}
							</div>
						) : (
							<div className="col-md-12">
								{picks.map((pick) => (
									<div key={"pn_" + pick.schedule.id} style={{ marginBottom: 10 }}>
										<label htmlFor={"enrollNotes_" + pick.schedule.id} style={{ position: "relative", display: "block" }}>
											{Constants.Common.signupNote} · {cursoCorto(pick.schedule.courseName)} — {horarioBonito(pick.schedule.name, { plural: true, separador: " " })}
											<span style={{ color: "var(--wa-oro)" }}> *</span>
										</label>
										<textarea id={"enrollNotes_" + pick.schedule.id} className="form-control" style={{ minHeight: 80 }} maxLength={2000} disabled={busy}
											placeholder={Constants.Common.signupNotePlaceholder} value={pick.notes}
											onChange={(event) => setPickNotes(pick.schedule.id, event.target.value)} />
										{intentoGuardar && pick.notes.trim() === ""
											? <div style={{ fontSize: 13, color: "var(--wa-oro)", marginTop: 4 }}>{Constants.Common.signupNoteRequired}</div>
											: ""}
										{/* Rotativo: cada horario elegido tiene sus propios hermanos (mismo módulo y periodo). */}
										<div style={{ marginTop: 8 }}>
											<RotationPicker
												idPrefix={"enrollRotation_" + pick.schedule.id}
												homeSchedule={pick.siblings?.homeSchedule ?? null}
												options={pick.siblings?.options ?? []}
												rotating={pick.rotating}
												scheduleIds={pick.authorizedScheduleIds}
												onChange={(nextRotating, nextIds) => updatePick(pick.schedule.id, { rotating: nextRotating, authorizedScheduleIds: nextIds })}
												disabled={busy}
												loading={pick.siblings === null}
											/>
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				) : ""}
			</Modal.Body>
			<Modal.Footer>
				<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onClose}>{Constants.Common.close}</button>
				{!hayEleccion || !faltanObligatorios ? "" : <span style={{ fontSize: 13, color: "var(--wa-apagado)", marginRight: "auto" }}>
					{Constants.Common.requiredFieldsHint}
				</span>}
				<button type="button" className="btn btn-primary rounded-pill" disabled={busy || success || !hayEleccion} onClick={enroll}>
					<FontAwesomeIcon icon={solid("user-plus")} />&nbsp;{Constants.Common.enroll}
				</button>
			</Modal.Footer>
		</Modal>
	);
};

export default EnrollStudentModal;
